import { PluginService } from '../../../../services/plugin.ts';
import { ToastService } from '../../../../services/toast.ts';
import { Plugin } from '../../../../models/types.ts';
import '../../../../components/library/ui/app-button.ts';
import '../../../../components/library/ui/app-badge.ts';
import '../../../../components/library/ui/app-modal.ts';
import '../../../../components/library/form/form-input.ts';
import '../../../../components/library/form/form-select.ts';
import '../../../../components/library/form/form-toggle.ts';
import '../../../../components/library/ui/code-editor.ts';
import '../../../../components/gateway/plugin-editor.ts';

export class GatewayPluginsTab extends HTMLElement {
  private _pluginService = PluginService.getInstance();
  private _toastService = ToastService.getInstance();
  private _orgId: string = '';
  private _plugins: Plugin[] = [];
  private _isLoading: boolean = false;
  private _editingPluginId: string | null = null;
  private _isCreating: boolean = false;

  // Filter state
  private _searchText: string = '';
  private _filterStatus: string = 'all';
  private _filterSide: string = 'all';

  static get observedAttributes() {
    return ['org-id'];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (name === 'org-id' && oldValue !== newValue) {
      this._orgId = newValue;
      this.loadPlugins();
    }
  }

  connectedCallback() {
    this.render();
  }

  async loadPlugins() {
    if (!this._orgId) return;
    this._isLoading = true;
    this.render();
    try {
      this._plugins = await this._pluginService.listPlugins(this._orgId) || [];
    } catch (e: any) {
      // Toast handled by ApiClient
      this._plugins = [];
    } finally {
      this._isLoading = false;
      this.render();
    }
  }

  private openEditor(id: string | null = null) {
    if (id === null) {
      this._isCreating = true;
      this._editingPluginId = null;
    } else {
      this._editingPluginId = id;
      this._isCreating = false;
    }
    this.render();
  }

  private closeEditor() {
    this._editingPluginId = null;
    this._isCreating = false;
    this.render();
  }

  private async handleDelete(pluginId: string) {
    if (!confirm('Are you sure you want to delete this plugin?')) return;
    try {
      await this._pluginService.deletePlugin(pluginId);
      await this.loadPlugins();
    } catch (e: any) {
      // Toast handled by ApiClient
    }
  }

  private getFilteredPlugins() {
    return this._plugins.filter(p => {
      const matchesSearch = !this._searchText || 
        p.name.toLowerCase().includes(this._searchText.toLowerCase()) ||
        p.type.toLowerCase().includes(this._searchText.toLowerCase());
      
      const matchesStatus = this._filterStatus === 'all' || 
        (this._filterStatus === 'active' && p.active) ||
        (this._filterStatus === 'inactive' && !p.active);
      
      const matchesSide = this._filterSide === 'all' || 
        p.side === this._filterSide;

      return matchesSearch && matchesStatus && matchesSide;
    });
  }

  private _renderTimeout: any = null;

  render() {
    if (this._renderTimeout) return;
    this._renderTimeout = setTimeout(() => {
        this._renderInternal();
        this._renderTimeout = null;
    }, 0);
  }

  private _renderInternal() {
    // Initialize structure once
    if (!this.querySelector('.plugins-container')) {
        this.innerHTML = `
            <div class="plugins-container"></div>
            <div class="editor-container"></div>
        `;
    }

    const pluginsContainer = this.querySelector('.plugins-container')!;
    const editorContainer = this.querySelector('.editor-container')!;

    if (this._isLoading) {
      pluginsContainer.innerHTML = `<div class="loader"></div>`;
      editorContainer.innerHTML = '';
      return;
    }

    if (this._editingPluginId !== null || this._isCreating) {
      editorContainer.innerHTML = `
        <gateway-plugin-editor 
          org-id="${this._orgId}" 
          ${this._editingPluginId ? `plugin-id="${this._editingPluginId}"` : ''}
        ></gateway-plugin-editor>
      `;
      
      const editor = editorContainer.querySelector('gateway-plugin-editor');
      editor?.addEventListener('close', () => this.closeEditor());
      editor?.addEventListener('save', () => {
        this.closeEditor();
        this.loadPlugins();
      });
      pluginsContainer.innerHTML = '';
      return;
    }

    editorContainer.innerHTML = '';

    const filtered = this.getFilteredPlugins();

    pluginsContainer.innerHTML = `
      <style>
        .filter-bar {
          display: flex;
          gap: 12px;
          margin-bottom: 24px;
          align-items: flex-end;
          flex-wrap: wrap;
        }
        .filter-bar form-input, .filter-bar form-select {
          margin-bottom: 0;
          min-width: 200px;
        }
        .plugin-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0 8px;
          font-family: var(--font-mono);
        }
        .plugin-table th {
          text-align: left;
          padding: 12px;
          color: var(--text-dim);
          font-size: 0.75rem;
          text-transform: uppercase;
        }
        .plugin-table td {
          padding: 16px 12px;
          background: rgba(255, 255, 255, 0.03);
          border-top: 1px solid var(--border-sketch);
          border-bottom: 1px solid var(--border-sketch);
          color: var(--text-main);
          vertical-align: middle;
        }
        .plugin-table td:first-child {
          border-left: 1px solid var(--border-sketch);
          border-radius: 4px 0 0 4px;
        }
        .plugin-table td:last-child {
          border-right: 1px solid var(--border-sketch);
          border-radius: 0 4px 4px 0;
        }
        .plugin-table tr:hover td {
          background: rgba(255, 255, 255, 0.05);
        }
        .plugin-name {
          font-weight: 700;
          font-family: var(--font-mono);
          font-size: 0.95rem;
        }
        .plugin-type {
          font-family: var(--font-mono);
          font-size: 0.75rem;
          color: var(--text-dim);
        }
        .action-cell {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 5rem 2rem;
          background: rgba(255, 255, 255, 0.02);
          border-radius: 16px;
          border: 1px dashed rgba(255, 255, 255, 0.1);
          text-align: center;
          grid-column: 1 / -1;
        }
      </style>

      <div class="filter-bar">
        <form-input placeholder="Search plugins..." value="${this._searchText}" id="filter-search"></form-input>
        <form-select id="filter-status" value="${this._filterStatus}" options='[
          {"value": "all", "label": "All Statuses"},
          {"value": "active", "label": "Active"},
          {"value": "inactive", "label": "Inactive"}
        ]'></form-select>
        <form-select id="filter-side" value="${this._filterSide}" options='[
          {"value": "all", "label": "All Sides"},
          {"value": "inbound", "label": "Inbound"},
          {"value": "outbound", "label": "Outbound"}
        ]'></form-select>
        <div style="flex: 1;"></div>
        <app-button variant="white" size="sm" icon="plus" id="btn-create-plugin" square data-tooltip="New Plugin"></app-button>
      </div>

      ${filtered.length > 0 ? `
        <div class="table-container">
          <table class="plugin-table">
            <thead>
              <tr>
                <th style="width: 100px;">Status</th>
                <th>Plugin</th>
                <th>Type</th>
                <th style="width: 120px;">Side</th>
                <th style="text-align: right;">Actions</th>
              </tr>
            </thead>
            <tbody>
              ${filtered.map(p => `
                <tr>
                  <td>
                    <app-badge variant="${p.active ? 'success' : 'neutral'}" size="sm">${p.active ? 'Active' : 'Inactive'}</app-badge>
                  </td>
                  <td>
                    <div class="plugin-name">${p.name}</div>
                  </td>
                  <td>
                    <span class="plugin-type">${p.type}</span>
                  </td>
                  <td>
                    <app-badge variant="neutral" size="sm" unshadowed>${p.side || 'inbound'}</app-badge>
                  </td>
                  <td style="width: 120px;">
                    <div class="action-cell">
                      <app-button variant="ghost" size="xs" icon="edit" data-edit-plugin="${p.id}" data-tooltip="Edit Plugin"></app-button>
                      <app-button variant="ghost" size="xs" icon="trash" data-delete-plugin="${p.id}" data-tooltip="Delete Plugin"></app-button>
                    </div>
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      ` : `
        <div class="empty-state">
          <div style="margin-bottom: 24px; position: relative; width: 120px; height: 120px; display: flex; align-items: center; justify-content: center;">
             <div style="position: absolute; width: 100%; height: 100%; background: var(--accent); opacity: 0.1; border-radius: 50%; filter: blur(20px);"></div>
             <app-icon name="plugin" size="xl" style="color: var(--accent); transform: scale(3); opacity: 0.8;"></app-icon>
          </div>
          <h3 style="color: var(--text-main); margin-bottom: 12px; font-family: var(--font-sketch); font-size: 1.5rem;">No Plugins Found</h3>
          <p style="color: var(--text-muted); max-width: 400px; line-height: 1.6;">Try adjusting your filters or create a new plugin to extend your gateway functionality.</p>
        </div>
      `}
    `;

    this.bindEvents();
  }

  private bindEvents() {
    this.querySelector('#btn-create-plugin')?.addEventListener('click', () => this.openEditor(null));

    this.querySelectorAll('[data-edit-plugin]').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-edit-plugin');
        if (id) this.openEditor(id);
      });
    });

    this.querySelectorAll('[data-delete-plugin]').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-delete-plugin');
        if (id) this.handleDelete(id);
      });
    });

    // Filter events
    const searchInput = this.querySelector('#filter-search');
    searchInput?.addEventListener('form-input', (e: any) => {
      this._searchText = e.detail.value;
      this.render();
      // Refocus input
      const input = this.querySelector('#filter-search') as any;
      if (input) {
        const inner = input.shadowRoot?.querySelector('input');
        if (inner) {
          inner.focus();
          inner.setSelectionRange(this._searchText.length, this._searchText.length);
        }
      }
    });

    this.querySelector('#filter-status')?.addEventListener('change', (e: any) => {
      this._filterStatus = e.detail.value;
      this.render();
    });

    this.querySelector('#filter-side')?.addEventListener('change', (e: any) => {
      this._filterSide = e.detail.value;
      this.render();
    });
  }
}

if (!customElements.get('gateway-plugins-tab')) {
  customElements.define('gateway-plugins-tab', GatewayPluginsTab);
}
